import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Inbox, Plus } from 'lucide-react';
import JobCard from '@/components/jobs/JobCard';

// Card columns respond to this board's OWN box width (container queries),
// not the viewport — the master-detail wide-screen pass (2026-09) can
// squeeze this board into a narrower column than the viewport suggests
// (list | detail split), and viewport-only breakpoints (the old
// `xl:grid-cols-3`) kept forcing 3 narrow cards per row in that squeezed
// space, mashing each card's Contract/Phase/Collected labels into an
// illegible overlap. Thresholds picked so each card keeps roughly its
// current ~420px of comfortable room regardless of context.
const BOARD_GRID_CLASSES = 'grid grid-cols-1 @4xl:grid-cols-2 @7xl:grid-cols-3 gap-3 sm:gap-4';

export const JobsBoardSkeleton = () => (
  <div className="@container">
  <div className={BOARD_GRID_CLASSES}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Card key={i} className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-4 w-2/3 bg-[hsl(var(--muted))]" />
          <Skeleton className="h-3 w-1/2 bg-[hsl(var(--muted))]" />
          <div className="grid grid-cols-3 gap-2">
            <Skeleton className="h-3 w-full bg-[hsl(var(--muted))]" />
            <Skeleton className="h-3 w-full bg-[hsl(var(--muted))]" />
            <Skeleton className="h-3 w-full bg-[hsl(var(--muted))]" />
          </div>
          <Skeleton className="h-1.5 w-full bg-[hsl(var(--muted))]" />
        </CardContent>
      </Card>
    ))}
  </div>
  </div>
);

export const JobsBoard = ({
  jobs = [],
  onOpenJob,
  onCreateJob,
  search = '',
  archived = false,
}) => {
  const { t } = useTranslation();
  if (!jobs.length) {
    return (
      <Card
        className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
        data-testid="jobs-empty"
      >
        <CardContent className="py-12 px-6 text-center">
          <div className="mx-auto size-10 rounded-md bg-[hsl(var(--muted))] border border-border flex items-center justify-center text-[hsl(var(--primary))]">
            <Inbox size={20} strokeWidth={1.75} />
          </div>
          <h3 className="mt-3 text-sm sm:text-base font-semibold text-foreground">
            {archived
              ? search
                ? t('jobs.archive.empty_match_title')
                : t('jobs.archive.empty_title')
              : search
              ? t('jobs.empty_states.no_match_title')
              : t('jobs.empty_states.no_jobs_title')}
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-muted-foreground max-w-sm mx-auto">
            {archived
              ? search
                ? t('jobs.archive.empty_match_help')
                : t('jobs.archive.empty_help')
              : search
              ? t('jobs.empty_states.no_match_help')
              : t('jobs.empty_states.no_jobs_help')}
          </p>
          {onCreateJob && !search && !archived ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onCreateJob}
              className="mt-4 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="jobs-empty-create"
            >
              <Plus size={14} className="mr-2" />
              {t('jobs.empty_states.create_button')}
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="@container">
      <div className={BOARD_GRID_CLASSES} data-testid="jobs-board">
        {jobs.map((job) => (
          <JobCard key={job.id} job={job} onOpen={onOpenJob} />
        ))}
      </div>
    </div>
  );
};

export default JobsBoard;
